import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AppContext from '../../../Context/AppContext';
import {
    getExigoUserIdFromSession,
    isV2AdminSession,
} from '../../../util/v2Admin';
import {
    areElearningImagesV2Enabled,
    fetchElearningImagesV2Flag,
    readJourneyDrawEnabled,
    saveElearningImagesV2Flag,
    subscribeElearningImagesV2,
    writeJourneyDrawEnabled,
} from '../../../util/portalV2Flags';
import './AdminLab.css';

/**
 * Dashboard V2 admin lab — visible only to whitelisted Exigo IDs.
 * Hosts account-reset link + personal/global feature toggles.
 */
const AdminLab = ({ onBack }) => {
    const { checkUserSession } = useContext(AppContext);
    const session = checkUserSession();
    const allowed = isV2AdminSession(session);
    const exigoId = getExigoUserIdFromSession(session);

    const [drawOn, setDrawOn] = useState(() => readJourneyDrawEnabled());
    const [imagesOn, setImagesOn] = useState(() =>
        areElearningImagesV2Enabled()
    );
    const [imagesBusy, setImagesBusy] = useState(false);
    const [notice, setNotice] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (!allowed || !session?.token) return undefined;
        let cancelled = false;
        (async () => {
            const value = await fetchElearningImagesV2Flag(session.token);
            if (!cancelled) setImagesOn(value);
        })();
        return () => {
            cancelled = true;
        };
    }, [allowed, session?.token]);

    useEffect(() => {
        return subscribeElearningImagesV2((value) => setImagesOn(value));
    }, []);

    if (!allowed) {
        return (
            <div className="dv2-adminlab">
                <button
                    type="button"
                    className="dv2-adminlab__back"
                    onClick={onBack}
                >
                    ← Back
                </button>
                <p className="dv2-adminlab__muted">
                    This lab is only available for V2 admin accounts.
                </p>
            </div>
        );
    }

    const onToggleDraw = (checked) => {
        writeJourneyDrawEnabled(checked);
        setDrawOn(checked);
        setNotice(
            checked
                ? 'Journey draw tools enabled on this browser. Open Goals on the dashboard to use them.'
                : 'Journey draw tools disabled.'
        );
        setError('');
    };

    const onToggleImages = async (checked) => {
        setImagesBusy(true);
        setError('');
        setNotice('');
        const result = await saveElearningImagesV2Flag(
            checked,
            session.token
        );
        setImagesBusy(false);
        if (!result.ok) {
            setError(
                result.status === 403
                    ? 'API rejected the update (admin Exigo id not allowed on server).'
                    : 'Could not save the global images flag. Check API deploy / PortalV2Flags.'
            );
            return;
        }
        setImagesOn(result.value);
        setNotice(
            result.value
                ? 'V2 e-learning images ON for all Portal V2 clients.'
                : 'V2 e-learning images OFF — V2 clients use legacy covers.'
        );
    };

    return (
        <div className="dv2-adminlab">
            <div className="dv2-adminlab__top">
                <button
                    type="button"
                    className="dv2-adminlab__back"
                    onClick={onBack}
                >
                    ← Back
                </button>
                <span className="dv2-adminlab__badge">V2 Admin</span>
            </div>

            <h1 className="dv2-adminlab__title">Lab</h1>
            <p className="dv2-adminlab__lead">
                Soft-launch tools for Exigo {exigoId}. Classic (V1) dashboard is
                unchanged.
            </p>

            <section className="dv2-adminlab__card">
                <h2>Account reset</h2>
                <p>
                    Open the reset console to rewind a whitelisted test account
                    to an earlier step.
                </p>
                <Link className="dv2-adminlab__cta" to="/dev/test-lab">
                    Open account reset console →
                </Link>
            </section>

            <section className="dv2-adminlab__card">
                <h2>Journey path tools</h2>
                <p>
                    Personal to this browser. When on, the Goals card shows draw
                    / edit path controls.
                </p>
                <label className="dv2-adminlab__check">
                    <input
                        type="checkbox"
                        checked={drawOn}
                        onChange={(e) => onToggleDraw(e.target.checked)}
                    />
                    <span>Enable draw path &amp; journey point tools</span>
                </label>
            </section>

            <section className="dv2-adminlab__card">
                <h2>E-learning V2 images</h2>
                <p>
                    Global for every Portal V2 client. When off, program and
                    module covers fall back to legacy (V1) media paths. V1
                    dashboard never uses this flag.
                </p>
                <label className="dv2-adminlab__check">
                    <input
                        type="checkbox"
                        checked={imagesOn}
                        disabled={imagesBusy}
                        onChange={(e) => onToggleImages(e.target.checked)}
                    />
                    <span>
                        Use V2 e-learning covers (programs + modules)
                        {imagesBusy ? '…' : ''}
                    </span>
                </label>
            </section>

            {notice ? (
                <p className="dv2-adminlab__notice" role="status">
                    {notice}
                </p>
            ) : null}
            {error ? (
                <p className="dv2-adminlab__error" role="alert">
                    {error}
                </p>
            ) : null}
        </div>
    );
};

export default AdminLab;
